import { defineModel } from "../define-model";
import type { RunStatus } from "./flow-run";

/** What a `waiting` node asks a person to decide. */
export type NodeWaitingFor = {
	kind: "approval";
	risk: "read" | "draft" | "outward" | "spend";
	action: string;
	title: string;
	summary: string;
	payload: Record<string, unknown>;
	/** `suggest`: the policy only allowed a suggestion; approving still runs it. */
	decisionMode: "approve" | "suggest";
	requestedAt: string;
};

export type NodeDecision = {
	approved: boolean;
	note?: string;
	decidedById: string | null;
	decidedAt: string;
};

export type NodeRun = {
	id: string;
	workspaceId: string;
	projectId: string | null;
	flowRunId: string;
	nodeId: string;
	nodeType: string;
	status: RunStatus;
	attempt: number;
	inputs: Record<string, unknown>;
	/** Port id → value; `_artifacts` maps ports to the artifacts holding them. */
	outputs: Record<string, unknown>;
	/** `[code] message`, e.g. `[insufficient_credits] Not enough credits…`. */
	error: string | null;
	model: string | null;
	generationId: string | null;
	usage: Record<string, number> | null;
	costUsd: number;
	millicredits: number;
	/** Absent on records cached before approvals existed. */
	waitingFor?: NodeWaitingFor | null;
	decision?: NodeDecision | null;
	startedAt: string | null;
	finishedAt: string | null;
	createdAt: string;
	updatedAt: string;
};

declare module "../types" {
	interface SyncModels {
		nodeRun: NodeRun;
	}
}

export function parseRunError(error: string | null | undefined) {
	if (!error) return null;
	const match = /^\[([\w-]+)\]\s*([\s\S]*)$/.exec(error);
	return match ? { code: match[1], message: match[2] } : { code: "error", message: error };
}

export const model = defineModel({
	name: "nodeRun",
	load: "lazy",
	indexes: ["flowRunId"],
});
